import React, { useState } from 'react';
import { Music, Sliders, Disc, Instagram, Music2, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { useLanguage } from '../context/LanguageContext';
import { artistInfo, services } from '../mock';
import { submitContactForm } from '../services/api';

const iconMap = {
  music: Music2,
  sliders: Sliders,
  disc: Disc
};

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_soundcraft-37/artifacts/kqf8nhzq_hngt%20preto.png';

const Home = () => {
  const { toast } = useToast();
  const { language, toggleLanguage, t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await submitContactForm(formData);
      toast({
        title: t.contact.success,
        description: t.contact.successMessage,
      });
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      toast({
        title: t.contact.error,
        description: t.contact.errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#E5E5E5]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/80 backdrop-blur-md border-b border-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <a href="#home" className="flex items-center gap-3">
            <img 
              src={LOGO_URL} 
              alt="HNGT Logo" 
              className="h-8 w-auto object-contain filter brightness-0 invert"
            />
          </a>
          <nav className="hidden md:flex gap-8 items-center">
            <a href="#services" className="text-[#E5E5E5] hover:text-[#7C3AED] transition-colors duration-300">
              {t.nav.services}
            </a>
            <a href="#about" className="text-[#E5E5E5] hover:text-[#7C3AED] transition-colors duration-300">
              {t.nav.about}
            </a>
            <a href="#contact" className="text-[#E5E5E5] hover:text-[#7C3AED] transition-colors duration-300">
              {t.nav.contact}
            </a>
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 text-[#E5E5E5] hover:text-[#7C3AED] transition-colors duration-300 px-3 py-1 rounded-full border border-[#2A2A2A] hover:border-[#7C3AED]"
            >
              <Globe size={16} />
              <span className="text-sm font-medium">{language.toUpperCase()}</span>
            </button>
          </nav>
          <div className="flex gap-4 items-center">
            <button
              onClick={toggleLanguage}
              className="md:hidden flex items-center gap-2 text-[#E5E5E5] hover:text-[#7C3AED] transition-colors duration-300"
            >
              <Globe size={20} />
            </button>
            <a 
              href={artistInfo.socialLinks.instagram} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#E5E5E5] hover:text-[#7C3AED] transition-colors duration-300"
            >
              <Instagram size={22} />
            </a>
            <a 
              href={artistInfo.socialLinks.spotify} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#E5E5E5] hover:text-[#7C3AED] transition-colors duration-300"
            >
              <Music size={22} />
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="min-h-screen flex items-center justify-center px-6 pt-20">
        <div className="max-w-7xl mx-auto text-center">
          <div className="mb-8">
            <img 
              src={LOGO_URL} 
              alt="HNGT Logo" 
              className="w-48 h-auto mx-auto mb-6 filter brightness-0 invert"
            />
          </div>
          <p className="text-xl md:text-2xl text-[#6B7280] mb-12 max-w-2xl mx-auto">
            {t.hero.tagline}
          </p>
          <Button 
            onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white px-8 py-6 text-lg rounded-full transition-all duration-300"
          >
            {t.hero.cta}
          </Button>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 px-6 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-center">
            {t.services.title}
          </h2>
          <p className="text-[#6B7280] text-center mb-16 max-w-2xl mx-auto">
            {t.services.subtitle}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="bg-[#1A1A1A] border-[#2A2A2A] hover:border-[#7C3AED] transition-all duration-300 group">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-[#7C3AED]/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-[#7C3AED]/20 transition-colors duration-300">
                  <Sliders className="w-8 h-8 text-[#7C3AED]" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-[#7C3AED]">{t.services.mixing.title}</h3>
                <p className="text-[#6B7280] leading-relaxed">{t.services.mixing.description}</p>
              </CardContent>
            </Card>
            
            <Card className="bg-[#1A1A1A] border-[#2A2A2A] hover:border-[#7C3AED] transition-all duration-300 group">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-[#7C3AED]/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-[#7C3AED]/20 transition-colors duration-300">
                  <Disc className="w-8 h-8 text-[#7C3AED]" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-[#7C3AED]">{t.services.mastering.title}</h3>
                <p className="text-[#6B7280] leading-relaxed">{t.services.mastering.description}</p>
              </CardContent>
            </Card>
            
            <Card className="bg-[#1A1A1A] border-[#2A2A2A] hover:border-[#7C3AED] transition-all duration-300 group">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-[#7C3AED]/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-[#7C3AED]/20 transition-colors duration-300">
                  <Music2 className="w-8 h-8 text-[#7C3AED]" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-[#7C3AED]">{t.services.coproduction.title}</h3>
                <p className="text-[#6B7280] leading-relaxed">{t.services.coproduction.description}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 px-6 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-12 text-center">
            {t.about.title}
          </h2>
          <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg p-8 md:p-12">
            {t.about.bio.map((paragraph, index) => (
              <p key={index} className="text-[#9CA3AF] leading-relaxed mb-6 last:mb-0">
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 px-6 bg-[#0A0A0A]">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-center">
            {t.contact.title}
          </h2>
          <p className="text-[#6B7280] text-center mb-12">
            {t.contact.subtitle}
          </p>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Input
                type="text"
                name="name"
                placeholder={t.contact.form.name}
                value={formData.name}
                onChange={handleInputChange}
                required
                className="bg-[#1A1A1A] border-[#2A2A2A] text-[#E5E5E5] placeholder:text-[#6B7280] focus:border-[#7C3AED] h-12"
              />
            </div>
            <div>
              <Input
                type="email"
                name="email"
                placeholder={t.contact.form.email}
                value={formData.email}
                onChange={handleInputChange}
                required
                className="bg-[#1A1A1A] border-[#2A2A2A] text-[#E5E5E5] placeholder:text-[#6B7280] focus:border-[#7C3AED] h-12"
              />
            </div>
            <div>
              <Textarea
                name="message"
                placeholder={t.contact.form.message}
                value={formData.message}
                onChange={handleInputChange}
                required
                rows={6}
                className="bg-[#1A1A1A] border-[#2A2A2A] text-[#E5E5E5] placeholder:text-[#6B7280] focus:border-[#7C3AED] resize-none"
              />
            </div>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#7C3AED] hover:bg-[#6D28D9] text-white py-6 text-lg rounded-full transition-all duration-300"
            >
              {isSubmitting ? t.contact.form.sending : t.contact.form.submit}
            </Button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-[#1A1A1A] bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-[#6B7280] text-sm">
            © 2025 {artistInfo.name}. {t.footer.copyright}
          </p>
          <div className="flex gap-6">
            <a 
              href={artistInfo.socialLinks.instagram} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#6B7280] hover:text-[#7C3AED] transition-colors duration-300"
            >
              <Instagram size={20} />
            </a>
            <a 
              href={artistInfo.socialLinks.spotify} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#6B7280] hover:text-[#7C3AED] transition-colors duration-300"
            >
              <Music size={20} />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;